import {
  WebsiteButton,
  WebsiteButtonLink,
  WebsiteCard,
  WebsiteContainer,
  WebsiteFooter,
  WebsiteHeading,
  WebsitePage,
  WebsitePageHeader,
  WebsiteSection,
  WebsiteText,
} from "@-/dev.improve.haniverse.components.website";
import { useEffect } from "react";
import { loadCrispChatWidget, openCrispChat } from "#load-crisp-chat-widget";

const SUPPORT_EMAIL = "hello@haniverse.app";

const SUPPORT_TITLE = "Support — Haniverse";
const SUPPORT_DESCRIPTION =
  "Get help with Haniverse: chat with us, email us, or request account deletion.";

/**
 * Public support page at `/support`, rendered on web only.
 *
 * The chat itself is Crisp's floating launcher, injected on mount — there is no in-page chat
 * container to render, so the page's job is to explain what to expect and give a mailto fallback
 * for anyone who blocks third-party scripts (the widget silently never appears for them).
 *
 * The web navigation layout bypasses the app providers for `/support`, so visiting it does
 * not initialize the app databases.
 */
export function SupportPage() {
  useEffect(() => {
    loadCrispChatWidget();
  }, []);

  return (
    <WebsitePage description={SUPPORT_DESCRIPTION} title={SUPPORT_TITLE}>
      <WebsitePageHeader
        description="Start a conversation with us using the chat button in the corner of this page. Ask about your account, billing, a character that looks wrong, or anything else — we read every message."
        eyebrow="Support"
        title="We’re here to help."
      />
      <main>
        <WebsiteSection spacing="compact">
          <WebsiteContainer className="grid gap-5 md:grid-cols-3">
            <WebsiteCard className="flex flex-col">
              <WebsiteHeading level={2} size="title">
                Chat with us
              </WebsiteHeading>
              <WebsiteText className="mt-3 flex-1">
                Use the chat bubble in the corner of this page. Leave your email in the chat and
                we’ll reply there even after you close the tab.
              </WebsiteText>
              <WebsiteButton className="mt-6 self-start" onClick={openCrispChat}>
                Start a chat
              </WebsiteButton>
            </WebsiteCard>
            <WebsiteCard className="flex flex-col">
              <WebsiteHeading level={2} size="title">
                Prefer email?
              </WebsiteHeading>
              <WebsiteText className="mt-3 flex-1">
                Write to {SUPPORT_EMAIL} and we’ll get back to you.
              </WebsiteText>
              <WebsiteButtonLink
                className="mt-6 self-start"
                href={`mailto:${SUPPORT_EMAIL}`}
                variant="secondary"
              >
                Email us
              </WebsiteButtonLink>
            </WebsiteCard>
            <WebsiteCard className="flex flex-col">
              <WebsiteHeading level={2} size="title">
                Delete your account
              </WebsiteHeading>
              <WebsiteText className="mt-3 flex-1">
                Request deletion of your account and its synced study data. No sign-in needed.
              </WebsiteText>
              <WebsiteButtonLink className="mt-6 self-start" href="/deleteme" variant="secondary">
                Request deletion
              </WebsiteButtonLink>
            </WebsiteCard>
          </WebsiteContainer>
        </WebsiteSection>
      </main>
      <WebsiteFooter />
    </WebsitePage>
  );
}
